import React from "react";
import { Link } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { useTranslation } from "react-i18next";
import { useTypographyHelpers } from "../../../contexts/DesignContext";
import Button from "../../ui/Button";
import LoadingSpinner from "../../ui/LoadingSpinner";
import SignInFormInput from "./SignInFormInput";

/**
 * Sign In Form Component
 */
const SignInForm = ({
  formData,
  formErrors,
  isLoading,
  submitError,
  successMessage,
  showPassword,
  handleChange,
  toggleShowPassword,
  handleSubmit,
  handleGoogleSuccess,
  handleGoogleError,
}) => {
  const { t } = useTranslation("auth");
  const { getSupportingText } = useTypographyHelpers();

  return (
    <div className="bg-gray-800/30 backdrop-blur border border-gray-700/50 rounded-3xl p-8">
      {/* Success Message from Signup */}
      {successMessage && (
        <div className="mb-6 bg-green-500/10 border border-green-500/20 rounded-2xl p-4">
          <p className="text-green-400 text-sm text-center">{successMessage}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email Input */}
        <SignInFormInput
          name="email"
          type="email"
          placeholder={t("signIn.email")}
          value={formData.email}
          onChange={handleChange}
          error={formErrors.email}
          disabled={isLoading}
        />

        {/* Password Input */}
        <SignInFormInput
          name="password"
          type={showPassword ? "text" : "password"}
          placeholder={t("signIn.password")}
          value={formData.password}
          onChange={handleChange}
          error={formErrors.password}
          showToggle={true}
          onToggleShow={toggleShowPassword}
          disabled={isLoading}
        />

        {/* Submit Error */}
        {submitError && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4">
            <p className="text-red-400 text-sm text-center">{submitError}</p>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="primary"
          color="violet"
          size="lg"
          disabled={isLoading}
          className="w-full flex items-center justify-center space-x-3"
        >
          {isLoading ? (
            <>
              <LoadingSpinner size="sm" />
              <span>{t("signIn.signInButton")}...</span>
            </>
          ) : (
            t("signIn.signInButton")
          )}
        </Button>
      </form>

      {/* Divider */}
      <div className="flex items-center my-6">
        <div className="flex-1 border-t border-gray-700/50"></div>
        <span className="px-4 text-gray-400 text-sm">
          {t("common.or", { defaultValue: "or" })}
        </span>
        <div className="flex-1 border-t border-gray-700/50"></div>
      </div>

      {/* Google Login Button */}
      <div className="flex justify-center">
        {process.env.REACT_APP_GOOGLE_CLIENT_ID ? (
          <div className="w-full flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme="filled_black"
              size="large"
              text="signin_with"
              shape="rectangular"
              logo_alignment="left"
              width="100%"
            />
          </div>
        ) : (
          <div className="w-full bg-red-500/10 border border-red-500/20 rounded-2xl p-4 text-center">
            <p className="text-red-400 text-sm">
              Google Client ID not configured. Please set
              REACT_APP_GOOGLE_CLIENT_ID in your .env file.
            </p>
          </div>
        )}
      </div>

      {/* Additional Links */}
      <div className="mt-6 space-y-4">
        {/* Forgot Password */}
        <div className="text-center">
          <Link
            to="/forgot-password"
            className="text-violet-400 hover:text-violet-300 transition-colors duration-300 text-sm"
          >
            {t("signIn.forgotPassword")}
          </Link>
        </div>

        {/* Sign Up Link */}
        <div className="text-center pt-4 border-t border-gray-700/50">
          <p className={`${getSupportingText("small")} text-gray-400 mb-2`}>
            {t("signIn.noAccount")}
          </p>
          <Link
            to="/signup"
            className="text-violet-400 hover:text-violet-300 transition-colors duration-300 font-medium"
          >
            {t("signIn.signUpLink")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignInForm;
